import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

const BOARD_MODEL_URL = "/design-preview/mirror-board.glb";
const PLAYER_COLOR_ANIMATIONS = new Set([
  "smart-cube",
  "locked-up-cubes",
  "rounded-cube",
  "reflective-cube",
  "companion-cube",
]);
const PREVIEW_PLAYER_COLORS = [
  "#005bff", // Player 1
  "#ff003d", // Player 2
  "#ffea00", // Player 3
  "#04ff17", // Player 4
  "#8b5cf6",
  "#00cfe8",
  "#ff7a00",
  "#e600b8",
  "#7ee000",
].map((color) => new THREE.Color(color));
const MIXER_MODEL_ANIMATIONS = new Set([
  "animated-rubiks-cube",
  "gear-cube",
]);
const PROCEDURAL_MODEL_ANIMATIONS = new Set([
  "liquid-glass-cube",
  "voxel-core-cube",
]);
const GEAR_MODEL_SCALE = 1.936;
const GEAR_ANIMATION_SPEED = 0.55;
const GEAR_SQUARED_HOLD_SECONDS = 3;
const DEFAULT_MODEL_SCALE = 0.88;
const BLOCK_POSITIONS = [
  [-5.25, 1.02, 3.15],
  [-2.15, 1.02, -5.25],
  [4.15, 1.02, 5.2],
  [5.2, 1.02, -1.15],
  [-3.25, 3.02, 2.15],
  [3.2, 3.02, -3.15],
  [1.25, 3.02, 3.25],
  [-1.25, 5.02, 1.25],
  [1.25, 5.02, -1.25],
];
const SPECIAL_POSITION = [5.2, 1.04, 2.1];

const resolveBlockTextureRepeat = (textureId) => {
  switch (String(textureId || "").trim().toLowerCase()) {
    case "paving-stones-138": return [1.44, 0.72];
    case "aged-brick": return [3.3, 3.3];
    case "paving-stones-076": return [1.75, 1.75];
    case "tiles-031": return [1.55, 1.55];
    case "tiles-100": return [1.45, 1.45];
    case "wood-floor-035": return [1.65, 1.2];
    default: return [1.35, 1.35];
  }
};

const normalizeMaterialName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const setMaterialStyle = (material, style = {}) => {
  if (!material || !style) return;
  if (style.color && material.color) material.color.set(style.color);
  if (Number.isFinite(style.metalness) && "metalness" in material) material.metalness = style.metalness;
  if (Number.isFinite(style.roughness) && "roughness" in material) material.roughness = style.roughness;
  if (style.emissive && material.emissive) {
    material.emissive.set(style.emissive);
    material.emissiveIntensity = Number.isFinite(style.emissiveIntensity) ? style.emissiveIntensity : 1;
  }
  if (Number.isFinite(style.opacity)) {
    material.opacity = style.opacity;
    material.transparent = style.opacity < 1;
    material.depthWrite = style.opacity >= 1;
    material.side = THREE.DoubleSide;
  }
};

const resolveAuthoredMaterialStyle = (animationId, rawMaterialName) => {
  const name = normalizeMaterialName(rawMaterialName);
  const exact = (entries) => entries[name] || null;

  switch (animationId) {
    case "smart-cube":
      return { metalness: 0.24, roughness: 0.32 };
    case "locked-up-cubes":
      return { metalness: 0.34, roughness: 0.38 };
    case "rounded-cube":
      return { metalness: 0.12, roughness: 0.3 };
    case "reflective-cube":
      return name.includes("glass")
        ? { color: "#c8f7ff", metalness: 0.04, roughness: 0.08, opacity: 0.22 }
        : { metalness: 0.68, roughness: 0.43 };
    case "companion-cube":
      return name.includes("glow")
        ? { color: "#fff4f8", metalness: 0.05, roughness: 0.45, emissive: "#ff9fca", emissiveIntensity: 1.2 }
        : { metalness: 0.18, roughness: 0.6 };
    case "puzzle-cube":
      return exact({
        blue: { color: "#2e80e8", metalness: 0.08, roughness: 0.38 },
        green: { color: "#66a82b", metalness: 0.08, roughness: 0.38 },
        magenta: { color: "#eb40db", metalness: 0.08, roughness: 0.38 },
        orange: { color: "#ff9933", metalness: 0.08, roughness: 0.38 },
        red: { color: "#ed403b", metalness: 0.08, roughness: 0.38 },
        yellow: { color: "#ffd133", metalness: 0.08, roughness: 0.38 },
        seam: { color: "#0b0b0e", metalness: 0.08, roughness: 0.38 },
      });
    case "animated-rubiks-cube":
      return exact({
        "material.001": { color: "#020305", metalness: 0.12, roughness: 0.3 },
        "material.002": { color: "#03b829", metalness: 0.04, roughness: 0.48 },
        "material.003": { color: "#ebf5ff", metalness: 0.04, roughness: 0.48 },
        "material.004": { color: "#eb0506", metalness: 0.04, roughness: 0.48 },
        "material.005": { color: "#0429f2", metalness: 0.04, roughness: 0.48 },
        "material.006": { color: "#ffd103", metalness: 0.04, roughness: 0.48 },
        "material.007": { color: "#ff4003", metalness: 0.04, roughness: 0.48 },
      });
    case "puzzle-cube-jigsaw":
      return exact({
        pink: { color: "#d31a96", metalness: 0.22, roughness: 0.52 },
        purple: { color: "#a025d5", metalness: 0.22, roughness: 0.52 },
        red: { color: "#ba3530", metalness: 0.22, roughness: 0.52 },
        brown: { color: "#845e42", metalness: 0.22, roughness: 0.52 },
        orange: { color: "#d5671f", metalness: 0.22, roughness: 0.52 },
        blue: { color: "#2863d7", metalness: 0.22, roughness: 0.52 },
        yellow: { color: "#d1b633", metalness: 0.22, roughness: 0.52 },
        green: { color: "#396e21", metalness: 0.22, roughness: 0.52 },
        core: { color: "#242528", metalness: 0.12, roughness: 0.52 },
      });
    case "abstract-spinning-cube": {
      if (name.includes("outline")) return { color: "#010205", metalness: 0.76, roughness: 0.64 };
      if (name.includes("white transparent")) return { color: "#b8f5ff", metalness: 0.08, roughness: 0.1, opacity: 0.22 };
      return exact({
        yellow: { color: "#ffd904", emissive: "#ffd904", emissiveIntensity: 1.4 },
        green: { color: "#05ff3d", emissive: "#05ff3d", emissiveIntensity: 1.4 },
        orange: { color: "#ff5704", emissive: "#ff5704", emissiveIntensity: 1.4 },
        red: { color: "#ff0608", emissive: "#ff0608", emissiveIntensity: 1.4 },
        cyan: { color: "#03f2ff", emissive: "#03f2ff", emissiveIntensity: 1.4 },
        blue: { color: "#0847ff", emissive: "#0847ff", emissiveIntensity: 1.4 },
        pink: { color: "#ff05a3", emissive: "#ff05a3", emissiveIntensity: 1.4 },
        purple: { color: "#9408ff", emissive: "#9408ff", emissiveIntensity: 1.4 },
      });
    }
    case "classic-rubik-cube":
      return exact({
        rubikred: { color: "#eb0506", metalness: 0.04, roughness: 0.52 },
        rubikorange: { color: "#ff4003", metalness: 0.04, roughness: 0.52 },
        rubikblue: { color: "#0429f2", metalness: 0.04, roughness: 0.52 },
        rubikgreen: { color: "#03b829", metalness: 0.04, roughness: 0.52 },
        rubikwhite: { color: "#ebf5ff", metalness: 0.04, roughness: 0.52 },
        rubikyellow: { color: "#ffd103", metalness: 0.04, roughness: 0.52 },
        rubikblack: { color: "#020305", metalness: 0.12, roughness: 0.7 },
      });
    case "mirror-rubik-silver":
      return name.includes("sticker")
        ? { color: "#cbd4dd", metalness: 0.88, roughness: 0.22 }
        : { color: "#06080b", metalness: 0.38, roughness: 0.6 };
    case "mirror-rubik-gold":
      return name.includes("sticker")
        ? { color: "#e4a51c", metalness: 0.86, roughness: 0.25 }
        : { color: "#090603", metalness: 0.38, roughness: 0.6 };
    case "burning-crystal-cube":
      return { color: "#2e0301", metalness: 0.34, roughness: 0.3, emissive: "#ff2404", emissiveIntensity: 2.8 };
    case "gear-cube":
      return exact({
        mtl: { color: "#708599", metalness: 0.92, roughness: 0.22 },
        mtl1: { color: "#b8610e", metalness: 0.9, roughness: 0.3 },
        mtl2: { color: "#051a21", metalness: 0.78, roughness: 0.28, emissive: "#05b8eb", emissiveIntensity: 1.15 },
      });
    case "voxel-core-cube":
      if (name.startsWith("voxelcore")) return { color: "#38f279", metalness: 0.05, roughness: 0.22, emissive: "#26ff6b", emissiveIntensity: 2.5 };
      if (name.startsWith("voxelmidnight")) return { color: "#05070f", metalness: 0.68, roughness: 0.42 };
      if (name.startsWith("voxelteal")) return { color: "#05524d", metalness: 0.52, roughness: 0.34 };
      if (name.startsWith("voxelpurple")) return { color: "#4f137a", metalness: 0.44, roughness: 0.38 };
      if (name.startsWith("voxelblack")) return { color: "#010203", metalness: 0.72, roughness: 0.36 };
      return null;
    case "water-tank":
      return exact({
        tank: { color: "#08addb", metalness: 0.08, roughness: 0.08, opacity: 0.34 },
        metal: { color: "#576b7a", metalness: 0.88, roughness: 0.28 },
        red: { color: "#b8090c", metalness: 0.38, roughness: 0.38 },
        white: { color: "#c7dbe6", metalness: 0.42, roughness: 0.28 },
        black: { color: "#020305", metalness: 0.7, roughness: 0.5 },
      });
    case "neon-cage-cube":
      return name.includes("teal")
        ? { color: "#045951", metalness: 0.7, roughness: 0.28, emissive: "#08d1b8", emissiveIntensity: 0.8 }
        : { color: "#38065c", metalness: 0.62, roughness: 0.32, emissive: "#b314ff", emissiveIntensity: 0.75 };
    case "impossible-cube":
      return name.startsWith("01")
        ? { color: "#01090b", metalness: 0.68, roughness: 0.46 }
        : { color: "#336104", metalness: 0.38, roughness: 0.24, emissive: "#8fff05", emissiveIntensity: 1.65 };
    case "one-line-cube":
      return { color: "#02131f", metalness: 0.6, roughness: 0.22, emissive: "#05b8ff", emissiveIntensity: 1.45 };
    case "liquid-glass-cube":
      return { color: "#0694eb", metalness: 0.08, roughness: 0.06, opacity: 0.52, emissive: "#032e57", emissiveIntensity: 0.72 };
    case "energy-crate":
      return { color: "#4d0201", metalness: 0.72, roughness: 0.42, emissive: "#14ff57", emissiveIntensity: 0.35 };
    case "sci-fi-crate":
      return { color: "#24313f", metalness: 0.74, roughness: 0.38 };
    case "sci-fi-cube":
      return { color: "#087f91", metalness: 1, roughness: 0.35, emissive: "#00d5ff", emissiveIntensity: 0.32 };
    default:
      return null;
  }
};

const createCompanionTintTexture = (sourceTexture, playerColor, textureCache, ownedTextures) => {
  const sourceImage = sourceTexture?.image;
  const width = sourceImage?.width || sourceImage?.videoWidth || 0;
  const height = sourceImage?.height || sourceImage?.videoHeight || 0;
  if (!sourceTexture || !sourceImage || !width || !height || typeof document === "undefined") return null;

  const cacheKey = `${sourceTexture.uuid}:${playerColor.getHexString()}`;
  if (textureCache.has(cacheKey)) return textureCache.get(cacheKey);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(sourceImage, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const target = playerColor.clone().convertLinearToSRGB();
    for (let offset = 0; offset < imageData.data.length; offset += 4) {
      const red = imageData.data[offset];
      const green = imageData.data[offset + 1];
      const blue = imageData.data[offset + 2];
      const maximum = Math.max(red, green, blue);
      const minimum = Math.min(red, green, blue);
      if (minimum < 190 || maximum - minimum > 24) continue;
      const brightness = (red + green + blue) / (3 * 255);
      imageData.data[offset] = Math.round(target.r * brightness * 255);
      imageData.data[offset + 1] = Math.round(target.g * brightness * 255);
      imageData.data[offset + 2] = Math.round(target.b * brightness * 255);
    }
    context.putImageData(imageData, 0, 0);
    const tinted = new THREE.CanvasTexture(canvas);
    tinted.colorSpace = THREE.SRGBColorSpace;
    tinted.flipY = sourceTexture.flipY;
    tinted.wrapS = sourceTexture.wrapS;
    tinted.wrapT = sourceTexture.wrapT;
    tinted.magFilter = sourceTexture.magFilter;
    tinted.minFilter = sourceTexture.minFilter;
    tinted.anisotropy = sourceTexture.anisotropy;
    tinted.needsUpdate = true;
    textureCache.set(cacheKey, tinted);
    ownedTextures.push(tinted);
    return tinted;
  } catch (_) {
    return null;
  }
};

// Mirrors Unity's FlagWallMeshBuilder: every vertical wall face gets upright
// UVs with whole 3:2 flags from left to right, one flag tall.
const FLAG_WALL_ASPECT = 1.5;
const applyFlagWallUvs = (mesh) => {
  const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
  const position = geometry.getAttribute("position");
  if (!position) return;
  mesh.updateMatrixWorld(true);
  const up = new THREE.Vector3(0, 1, 0);
  const world = Array.from({ length: position.count }, (_, index) =>
    new THREE.Vector3().fromBufferAttribute(position, index).applyMatrix4(mesh.matrixWorld),
  );
  const shadingNormals = geometry.getAttribute("normal");
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
  const uvs = new Float32Array(position.count * 2);
  const faces = new Map();
  for (let index = 0; index + 2 < position.count; index += 3) {
    const normal = new THREE.Vector3()
      .subVectors(world[index + 1], world[index])
      .cross(new THREE.Vector3().subVectors(world[index + 2], world[index]))
      .normalize();
    if (shadingNormals) {
      const shading = new THREE.Vector3();
      for (let k = 0; k < 3; k += 1) shading.add(new THREE.Vector3().fromBufferAttribute(shadingNormals, index + k));
      if (shading.applyMatrix3(normalMatrix).dot(normal) < 0) normal.negate();
    }
    if (!Number.isFinite(normal.x) || Math.abs(normal.y) > 0.35) continue;
    normal.y = 0;
    normal.normalize();
    const key = [
      Math.round(normal.x * 4),
      Math.round(normal.z * 4),
      Math.round(world[index].dot(normal) * 50),
    ].join(":");
    if (!faces.has(key)) faces.set(key, { normal, triangles: [] });
    faces.get(key).triangles.push(index);
  }
  faces.forEach(({ normal, triangles }) => {
    const right = new THREE.Vector3().crossVectors(up, normal).normalize();
    let minR = Infinity;
    let maxR = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    triangles.forEach((start) => {
      for (let k = 0; k < 3; k += 1) {
        const point = world[start + k];
        const r = point.dot(right);
        minR = Math.min(minR, r);
        maxR = Math.max(maxR, r);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    });
    const height = Math.max(0.0001, maxY - minY);
    const length = Math.max(0.0001, maxR - minR);
    const naturalWidth = FLAG_WALL_ASPECT * height;
    const flagCount = Math.round(length / naturalWidth);
    const centerR = (minR + maxR) / 2;
    triangles.forEach((start) => {
      for (let k = 0; k < 3; k += 1) {
        const point = world[start + k];
        const r = point.dot(right);
        uvs[(start + k) * 2] = flagCount >= 1
          ? ((r - minR) / length) * flagCount
          : 0.5 + (r - centerR) / naturalWidth;
        uvs[(start + k) * 2 + 1] = 0.004 + 0.992 * ((point.y - minY) / height);
      }
    });
  });
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  mesh.geometry = geometry;
};

const loadTexture = (loader, source, { colorSpace = true, repeat = 1 } = {}) =>
  new Promise((resolve) => {
    const url = String(source || "").trim();
    if (!url) {
      resolve(null);
      return;
    }
    loader.load(
      url,
      (texture) => {
        if (colorSpace) texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        const repeatX = Array.isArray(repeat) ? Number(repeat[0] || 1) : Number(repeat || 1);
        const repeatY = Array.isArray(repeat) ? Number(repeat[1] || repeatX) : repeatX;
        texture.repeat.set(repeatX, repeatY);
        texture.anisotropy = 8;
        texture.needsUpdate = true;
        resolve(texture);
      },
      undefined,
      () => resolve(null),
    );
  });

const makeMaterialSet = ({ color, texture, normalMap, roughnessMap, metalnessMap, special }) =>
  Array.from({ length: 6 }, () =>
    new THREE.MeshStandardMaterial({
      color,
      map: texture,
      normalMap,
      roughnessMap,
      metalnessMap,
      metalness: special ? 0.12 : metalnessMap ? 0.48 : 0.18,
      roughness: special ? 0.26 : roughnessMap ? 0.52 : 0.34,
    }),
  );

const addPreviewCube = ({
  scene,
  position,
  size = 0.88,
  color,
  texture,
  normalMap,
  roughnessMap,
  metalnessMap,
  special = false,
}) => {
  const geometry = new THREE.BoxGeometry(size, size, size, 2, 2, 2);
  const materials = makeMaterialSet({
    color,
    texture,
    normalMap,
    roughnessMap,
    metalnessMap,
    special,
  });
  const cube = new THREE.Mesh(geometry, materials);
  cube.position.set(...position);
  cube.castShadow = true;
  cube.receiveShadow = true;
  scene.add(cube);
  return cube;
};

const addSpecialMarkers = (scene, position, size, texture, ownedMaterials, glow = false) => {
  const markerSize = size * 0.7;
  const offset = size * 0.505;
  const faces = [
    { offset: [0, 0, offset], rotation: [0, 0, 0] },
    { offset: [0, 0, -offset], rotation: [0, Math.PI, 0] },
    { offset: [offset, 0, 0], rotation: [0, Math.PI / 2, 0] },
    { offset: [-offset, 0, 0], rotation: [0, -Math.PI / 2, 0] },
    { offset: [0, offset, 0], rotation: [-Math.PI / 2, 0, 0] },
    { offset: [0, -offset, 0], rotation: [Math.PI / 2, 0, 0] },
  ];
  const markers = [];
  faces.forEach((face, index) => {
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: texture ? 0xffffff : 0xffdd45,
      transparent: true,
      alphaTest: 0.02,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      side: THREE.DoubleSide,
      blending: glow ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    material.toneMapped = !glow;
    ownedMaterials.push(material);
    const marker = new THREE.Mesh(new THREE.PlaneGeometry(markerSize, markerSize), material);
    marker.name = `Bloque especial cara ${index + 1}`;
    marker.position.set(
      position[0] + face.offset[0],
      position[1] + face.offset[1],
      position[2] + face.offset[2],
    );
    marker.rotation.set(...face.rotation);
    marker.renderOrder = 8;
    scene.add(marker);
    markers.push(marker);
  });
  return markers;
};

const cloneAnimationModel = (
  source,
  position,
  animationId,
  ownedMaterials,
  instanceIndex,
  companionTextureCache,
  ownedTextures,
) => {
  const clone = source.clone(true);
  clone.position.set(...position);
  clone.scale.multiplyScalar(animationId === "gear-cube" ? GEAR_MODEL_SCALE : DEFAULT_MODEL_SCALE);
  clone.name = `${animationId} preview`;
  clone.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
    const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
    const materials = sourceMaterials.map((material) => {
      const next = material.clone();
      const normalizedName = normalizeMaterialName(next.name);
      setMaterialStyle(next, resolveAuthoredMaterialStyle(animationId, next.name));
      if (PLAYER_COLOR_ANIMATIONS.has(animationId)) {
        const playerColor = PREVIEW_PLAYER_COLORS[instanceIndex % PREVIEW_PLAYER_COLORS.length];
        const shouldTint = animationId === "reflective-cube"
          ? normalizedName.includes("shell")
          : animationId === "companion-cube"
            ? !normalizedName.includes("glow")
            : true;
        if (shouldTint) {
          // Player colors are identity colors in XIMA. Keep them out of the
          // cinematic tone-mapping pass so the preview does not wash saturated
          // red, blue, yellow and green into pale/white variants.
          next.toneMapped = false;
          if (animationId === "companion-cube" && next.map) {
            const tintedMap = createCompanionTintTexture(
              next.map,
              playerColor,
              companionTextureCache,
              ownedTextures,
            );
            if (tintedMap) {
              next.map = tintedMap;
              next.color.set(0xffffff);
            } else {
              next.color.copy(playerColor);
            }
          } else {
            next.color.copy(playerColor);
          }
        }
      }
      next.needsUpdate = true;
      ownedMaterials.push(next);
      return next;
    });
    child.material = Array.isArray(child.material) ? materials : materials[0];
  });
  return clone;
};

const seededUnit = (seed) => {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const addLiquidAnimation = ({ model, phase, ownedMaterials, proceduralAnimators }) => {
  const liquidRoot = model.getObjectByName("XIMA_PREVIEW_ROOT") || model;
  const liquidMaterials = [];

  model.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      material.color?.lerp?.(new THREE.Color(0x72eaff), 0.28);
      material.transparent = true;
      material.opacity = Math.min(Number.isFinite(material.opacity) ? material.opacity : 1, 0.72);
      material.depthWrite = false;
      material.side = THREE.DoubleSide;
      if ("roughness" in material) material.roughness = 0.08;
      if ("metalness" in material) material.metalness = 0.02;
      if ("transmission" in material) material.transmission = Math.max(material.transmission || 0, 0.62);

      const liquidUniforms = {
        time: { value: 0 },
        phase: { value: phase },
      };
      material.onBeforeCompile = (shader) => {
        shader.uniforms.ximaLiquidTime = liquidUniforms.time;
        shader.uniforms.ximaLiquidPhase = liquidUniforms.phase;
        shader.vertexShader = `uniform float ximaLiquidTime;\nuniform float ximaLiquidPhase;\n${shader.vertexShader}`;
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `vec3 transformed = vec3(position);
          float ximaTopMask = smoothstep(-0.18, 0.94, position.y);
          float ximaWaveA = sin(position.x * 5.2 + ximaLiquidTime * 1.85 + ximaLiquidPhase);
          float ximaWaveB = cos(position.z * 6.1 - ximaLiquidTime * 1.45 + ximaLiquidPhase * 0.7);
          transformed.y += ximaTopMask * (ximaWaveA * 0.052 + ximaWaveB * 0.036);
          transformed.x += ximaTopMask * sin(position.z * 4.3 + ximaLiquidTime * 0.9) * 0.012;
          transformed.z += ximaTopMask * cos(position.x * 4.7 - ximaLiquidTime) * 0.012;`,
        );
      };
      material.customProgramCacheKey = () => "xima-liquid-particles-v1";
      material.needsUpdate = true;
      liquidMaterials.push({ material, uniforms: liquidUniforms });
    });
  });

  const particleCount = 42;
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);
  for (let index = 0; index < particleCount; index += 1) {
    const offset = index * 3;
    positions[offset] = (seededUnit(index + phase * 17.3) - 0.5) * 1.34;
    positions[offset + 1] = -0.58 + seededUnit(index + phase * 31.7) * 1.08;
    positions[offset + 2] = (seededUnit(index + phase * 47.9) - 0.5) * 1.34;
    velocities[offset] = (seededUnit(index + phase * 59.1) - 0.5) * 0.24;
    velocities[offset + 1] = (seededUnit(index + phase * 71.3) - 0.5) * 0.18;
    velocities[offset + 2] = (seededUnit(index + phase * 83.7) - 0.5) * 0.24;
  }

  const particleGeometry = new THREE.BufferGeometry();
  particleGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const particleMaterial = new THREE.PointsMaterial({
    color: 0xb7fbff,
    size: 0.095,
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  ownedMaterials.push(particleMaterial);
  const particles = new THREE.Points(particleGeometry, particleMaterial);
  particles.name = "XIMA liquid particles";
  particles.frustumCulled = false;
  particles.renderOrder = 6;
  liquidRoot.add(particles);

  const lowerBound = -0.72;
  const upperBound = 0.7;
  const sideBound = 0.72;
  const separationRadius = 0.17;
  proceduralAnimators.push((delta, elapsed) => {
    liquidMaterials.forEach(({ uniforms }) => {
      uniforms.time.value = elapsed;
    });

    const lateralX = Math.sin(elapsed * 0.82 + phase) * 0.42;
    const lateralZ = Math.cos(elapsed * 0.67 + phase * 0.8) * 0.34;
    for (let left = 0; left < particleCount; left += 1) {
      const leftOffset = left * 3;
      for (let right = left + 1; right < particleCount; right += 1) {
        const rightOffset = right * 3;
        const dx = positions[rightOffset] - positions[leftOffset];
        const dy = positions[rightOffset + 1] - positions[leftOffset + 1];
        const dz = positions[rightOffset + 2] - positions[leftOffset + 2];
        const distanceSquared = dx * dx + dy * dy + dz * dz;
        if (distanceSquared <= 0.000001 || distanceSquared >= separationRadius * separationRadius) continue;
        const distance = Math.sqrt(distanceSquared);
        const impulse = ((separationRadius - distance) / separationRadius) * delta * 1.8;
        const invDistance = 1 / distance;
        const impulseX = dx * invDistance * impulse;
        const impulseY = dy * invDistance * impulse;
        const impulseZ = dz * invDistance * impulse;
        velocities[leftOffset] -= impulseX;
        velocities[leftOffset + 1] -= impulseY;
        velocities[leftOffset + 2] -= impulseZ;
        velocities[rightOffset] += impulseX;
        velocities[rightOffset + 1] += impulseY;
        velocities[rightOffset + 2] += impulseZ;
      }
    }

    for (let index = 0; index < particleCount; index += 1) {
      const offset = index * 3;
      velocities[offset] += (lateralX - positions[offset] * 0.2) * delta;
      velocities[offset + 1] += (-0.62 - positions[offset + 1] * 0.08) * delta;
      velocities[offset + 2] += (lateralZ - positions[offset + 2] * 0.2) * delta;
      velocities[offset] *= 0.989;
      velocities[offset + 1] *= 0.989;
      velocities[offset + 2] *= 0.989;
      positions[offset] += velocities[offset] * delta;
      positions[offset + 1] += velocities[offset + 1] * delta;
      positions[offset + 2] += velocities[offset + 2] * delta;

      if (positions[offset] < -sideBound || positions[offset] > sideBound) {
        positions[offset] = THREE.MathUtils.clamp(positions[offset], -sideBound, sideBound);
        velocities[offset] *= -0.58;
      }
      if (positions[offset + 1] < lowerBound || positions[offset + 1] > upperBound) {
        positions[offset + 1] = THREE.MathUtils.clamp(positions[offset + 1], lowerBound, upperBound);
        velocities[offset + 1] *= -0.42;
      }
      if (positions[offset + 2] < -sideBound || positions[offset + 2] > sideBound) {
        positions[offset + 2] = THREE.MathUtils.clamp(positions[offset + 2], -sideBound, sideBound);
        velocities[offset + 2] *= -0.58;
      }
    }
    particleGeometry.attributes.position.needsUpdate = true;
  });
};

const addVoxelAnimation = ({ model, phase, proceduralAnimators }) => {
  const shell = model.getObjectByName("VoxelShell");
  const core = model.getObjectByName("VoxelCore");
  if (!shell || !core) return;

  const baseShellQuaternion = shell.quaternion.clone();
  const baseShellScale = shell.scale.clone();
  const baseCoreScale = core.scale.clone();
  const spinQuaternion = new THREE.Quaternion();
  const tiltQuaternion = new THREE.Quaternion();
  const yAxis = new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3(1, 0, 0);
  const direction = Math.floor(phase) % 2 === 0 ? 1 : -1;
  const coreMaterials = [];
  core.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (material.emissive) {
        material.emissive.set(0x1de5ff);
        coreMaterials.push(material);
      }
    });
  });

  proceduralAnimators.push((delta, elapsed) => {
    const pulse = 1 + Math.sin(elapsed * 2.35 + phase) * 0.13;
    const shellPulse = 1 + Math.sin(elapsed * 1.45 + phase * 0.6) * 0.035;
    spinQuaternion.setFromAxisAngle(yAxis, direction * elapsed * 0.72);
    tiltQuaternion.setFromAxisAngle(xAxis, Math.sin(elapsed * 0.83 + phase) * 0.12);
    shell.quaternion.copy(baseShellQuaternion).multiply(spinQuaternion).multiply(tiltQuaternion);
    shell.scale.copy(baseShellScale).multiplyScalar(shellPulse);
    core.scale.copy(baseCoreScale).multiplyScalar(pulse);
    coreMaterials.forEach((material) => {
      material.emissiveIntensity = 1.15 + pulse * 0.85;
    });
  });
};

const addProceduralAnimation = ({ model, animationId, phase, ownedMaterials, proceduralAnimators }) => {
  if (animationId === "liquid-glass-cube") {
    addLiquidAnimation({ model, phase, ownedMaterials, proceduralAnimators });
  } else if (animationId === "voxel-core-cube") {
    addVoxelAnimation({ model, phase, proceduralAnimators });
  }
};

// ---------------------------------------------------------------------------
// Modo Diseñar v2: tablero por partes, igual que en el juego (Unity).
// ---------------------------------------------------------------------------

const BOARD_PITCH = 12.9 / 13;
const ATLAS_GRID = 4;
const WALL_TEXTURE_PER_CELL = 0.28;
const FLOOR_TOPS = [0.5, 2.5, 4.5, 6.5];

let boardGltfPromise = null;
const loadBoardGltf = (loader) => {
  if (!boardGltfPromise) {
    boardGltfPromise = loader.loadAsync(BOARD_MODEL_URL).catch((error) => {
      boardGltfPromise = null;
      throw error;
    });
  }
  return boardGltfPromise;
};

const floorIndexForY = (y) => {
  let best = 0;
  FLOOR_TOPS.forEach((top, index) => {
    if (Math.abs(top - y) < Math.abs(FLOOR_TOPS[best] - y)) best = index;
  });
  return best;
};

// Igual que BoardDesignVariant en Unity.
const tileVariant = (col, row, floor) => {
  let h = (Math.imul((col + 64) >>> 0, 73856093) ^ Math.imul((row + 64) >>> 0, 19349663) ^ Math.imul((floor + 7) >>> 0, 83492791)) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h % (ATLAS_GRID * ATLAS_GRID);
};

const applyTileUvs = (geometry) => {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute("position");
  const uvs = new Float32Array(position.count * 2);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (let index = 0; index + 2 < position.count; index += 3) {
    a.fromBufferAttribute(position, index);
    b.fromBufferAttribute(position, index + 1);
    c.fromBufferAttribute(position, index + 2);
    const cx = (a.x + b.x + c.x) / 3;
    const cy = (a.y + b.y + c.y) / 3;
    const cz = (a.z + b.z + c.z) / 3;
    const col = Math.round(cx / BOARD_PITCH);
    const row = Math.round(cz / BOARD_PITCH);
    const variant = tileVariant(col, row, floorIndexForY(cy));
    const ax = variant % ATLAS_GRID;
    const ay = Math.floor(variant / ATLAS_GRID);
    [a, b, c].forEach((point, k) => {
      const lu = THREE.MathUtils.clamp((point.x - col * BOARD_PITCH) / BOARD_PITCH + 0.5, 0, 1);
      const lv = THREE.MathUtils.clamp((point.z - row * BOARD_PITCH) / BOARD_PITCH + 0.5, 0, 1);
      uvs[(index + k) * 2] = (ax + lu) / ATLAS_GRID;
      uvs[(index + k) * 2 + 1] = (ay + lv) / ATLAS_GRID;
    });
  }
  source.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  return source;
};

// Casillas tapadas por el piso de arriba (como "Casillas Cubiertas" en Unity):
// las del piso 1 bajo el piso 2 y las del piso 2 bajo el piso 3. La del centro
// del piso 3 (bajo la columna G7) se queda como está.
const splitCoveredTiles = (geometry) => {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute("position");
  const visible = [];
  const covered = [];
  const p = new THREE.Vector3();
  for (let index = 0; index + 2 < position.count; index += 3) {
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let k = 0; k < 3; k += 1) {
      p.fromBufferAttribute(position, index + k);
      cx += p.x / 3;
      cy += p.y / 3;
      cz += p.z / 3;
    }
    const floor = floorIndexForY(cy);
    const ring = Math.max(Math.abs(Math.round(cx / BOARD_PITCH)), Math.abs(Math.round(cz / BOARD_PITCH)));
    const target = floor <= 1 && ring <= 4 - 2 * floor ? covered : visible;
    for (let k = 0; k < 3; k += 1) {
      p.fromBufferAttribute(position, index + k);
      target.push(p.x, p.y, p.z);
    }
  }
  const build = (values) => {
    const result = new THREE.BufferGeometry();
    result.setAttribute("position", new THREE.Float32BufferAttribute(values, 3));
    result.computeVertexNormals();
    return result;
  };
  return { visible: build(visible), covered: build(covered) };
};

const applyPlanarWallUvs = (geometry) => {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute("position");
  const uvs = new Float32Array(position.count * 2);
  const scale = WALL_TEXTURE_PER_CELL / BOARD_PITCH;
  const up = new THREE.Vector3(0, 1, 0);
  const pts = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  const normal = new THREE.Vector3();
  const right = new THREE.Vector3();
  for (let index = 0; index + 2 < position.count; index += 3) {
    pts.forEach((point, k) => point.fromBufferAttribute(position, index + k));
    normal.subVectors(pts[1], pts[0]).cross(new THREE.Vector3().subVectors(pts[2], pts[0])).normalize();
    const horizontal = Math.abs(normal.y) > 0.5;
    if (!horizontal) {
      normal.y = 0;
      normal.normalize();
      right.crossVectors(up, normal).normalize();
    }
    pts.forEach((point, k) => {
      uvs[(index + k) * 2] = horizontal ? point.x * scale : point.dot(right) * scale;
      uvs[(index + k) * 2 + 1] = horizontal ? point.z * scale : point.y * scale;
    });
  }
  source.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  return source;
};

// Separa las molduras del borde de cada piso de las líneas entre casillas.
const splitTrimGeometry = (geometry) => {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute("position");
  const perimeter = [];
  const strips = [];
  const wallStrips = [];
  const p = new THREE.Vector3();
  const q = new THREE.Vector3();
  const r = new THREE.Vector3();
  for (let index = 0; index + 2 < position.count; index += 3) {
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let k = 0; k < 3; k += 1) {
      p.fromBufferAttribute(position, index + k);
      cx += p.x / 3;
      cy += p.y / 3;
      cz += p.z / 3;
    }
    p.fromBufferAttribute(position, index);
    q.fromBufferAttribute(position, index + 1);
    r.fromBufferAttribute(position, index + 2);
    const normalY = Math.abs(new THREE.Vector3().subVectors(q, p).cross(new THREE.Vector3().subVectors(r, p)).normalize().y);
    const d = Math.max(Math.abs(cx), Math.abs(cz)) / BOARD_PITCH;
    // Igual que ClassifyBoardDesignEdge en Unity.
    let kind = 2;
    FLOOR_TOPS.some((top, floor) => {
      const half = 6.5 - 2 * floor;
      if (Math.abs(d - half) >= 0.09 || cy <= top - 2.2 * BOARD_PITCH || cy >= top + 0.15 * BOARD_PITCH) return false;
      kind = Math.abs(cy - top) < 0.1 * BOARD_PITCH ? 0 : normalY < 0.5 ? 1 : 2;
      return true;
    });
    const target = kind === 0 ? perimeter : kind === 1 ? wallStrips : strips;
    for (let k = 0; k < 3; k += 1) {
      p.fromBufferAttribute(position, index + k);
      target.push(p.x, p.y, p.z);
    }
  }
  const build = (values) => {
    const result = new THREE.BufferGeometry();
    result.setAttribute("position", new THREE.Float32BufferAttribute(values, 3));
    result.computeVertexNormals();
    return result;
  };
  return { perimeter: build(perimeter), strips: build(strips), wallStrips: build(wallStrips) };
};

const isOrig = (value) => !value || value === "orig";

const FINISHES = {
  polished: { roughness: 0.15, clearcoat: 0.7, clearcoatRoughness: 0.08 },
  varnish: { roughness: 0.3, clearcoat: 0.35, clearcoatRoughness: 0.2 },
  matte: { roughness: 0.88, clearcoat: 0, normalScale: 1.3 },
  mirror: { roughness: 0.04, clearcoat: 1, clearcoatRoughness: 0.02, normalScale: 0.6 },
  lava: { roughness: 0.45, emissive: true },
  gloss: { roughness: 0.18, clearcoat: 0.5 },
};

const TRIM_COLORS = { gold: "#D4AF37", brass: "#B8893C", silver: "#D9DEE6" };
const LABEL_COLORS = { gold: "#E8C66A", brass: "#E2BD70", silver: "#E6EDF5" };
const LABEL_FONTS = {
  classic: "900 150px 'Arial Black', Arial, sans-serif",
  bold: "900 150px 'Roboto', 'Arial Black', sans-serif",
  mono: "700 150px 'Roboto Mono', 'Courier New', monospace",
  light: "700 150px 'Lato', Arial, sans-serif",
};

// Con el borde de cristal las letras son las de siempre del juego (Stage.cs):
// clásica negra con borde amarillo, gruesa blanca, máquina amarilla, brillante cian.
const CLASSIC_LABEL_STYLES = {
  classic: { fill: "#000000", stroke: "#fff496" },
  bold: { fill: "#ffffff", stroke: "#000000" },
  mono: { fill: "#ffd600", stroke: "#14141e" },
  light: { fill: "#78ecff", stroke: "#04163c" },
};

const makeLabelTexture = (text, color, fontId, cache, owned, stroke = "rgba(12,9,4,0.9)") => {
  const key = `${text}|${color}|${fontId}|${stroke}`;
  if (cache.has(key)) return cache.get(key);
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  context.font = LABEL_FONTS[fontId] || LABEL_FONTS.classic;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineWidth = stroke === "rgba(12,9,4,0.9)" ? 10 : 22;
  context.lineJoin = "round";
  context.strokeStyle = stroke;
  context.strokeText(text, 128, 136);
  context.fillStyle = color;
  context.fillText(text, 128, 136);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  cache.set(key, texture);
  owned.push(texture);
  return texture;
};

// Letras A–M y números 1–13 pintados en las paredes, debajo de su casilla.
const addWallLabels = ({ scene, trim, fontId, scale, ownedMaterials, ownedTextures, floating = false }) => {
  const classicStyle = CLASSIC_LABEL_STYLES[fontId] || CLASSIC_LABEL_STYLES.classic;
  const color = floating ? classicStyle.fill : LABEL_COLORS[trim] || LABEL_COLORS.gold;
  const stroke = floating ? classicStyle.stroke : undefined;
  const cache = new Map();
  const group = new THREE.Group();
  group.name = "Letras y números";
  const floors = [
    { min: 1, max: 13 },
    { min: 3, max: 11 },
    { min: 5, max: 9 },
    { min: 7, max: 7 },
  ];
  const sizeScale = THREE.MathUtils.clamp(Number(scale) || 1, 0.6, floating ? 2.2 : 1.45);
  floors.forEach((floor, index) => {
    // Con cristal: como en el juego, junto a cada piso y siempre por delante.
    if (floating && floor.min === floor.max) return;
    const top = FLOOR_TOPS[index];
    const y = !floating && index === 0 ? top - 0.47 * BOARD_PITCH : top - 0.3 * BOARD_PITCH;
    const half = (floor.max - 7) * BOARD_PITCH + (floating ? 0.58 : 0.56 * BOARD_PITCH + 0.012);
    const size = (floating ? 0.5 : index === 0 ? 0.62 : 0.56) * sizeScale;
    const place = (text, x, z, rotationY) => {
      const material = new THREE.MeshBasicMaterial({
        map: makeLabelTexture(text, color, fontId, cache, ownedTextures, stroke),
        transparent: true,
        depthWrite: false,
        depthTest: !floating,
        polygonOffset: true,
        polygonOffsetFactor: -2,
      });
      ownedMaterials.push(material);
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
      mesh.position.set(x, y, z);
      mesh.rotation.y = rotationY;
      mesh.renderOrder = 4;
      group.add(mesh);
    };
    for (let col = floor.min; col <= floor.max; col += 1) {
      const letter = String.fromCharCode(64 + col);
      const x = (col - 7) * BOARD_PITCH;
      place(letter, x, half, 0);
      place(letter, x, -half, Math.PI);
    }
    for (let row = floor.min; row <= floor.max; row += 1) {
      const z = (row - 7) * BOARD_PITCH;
      place(String(row), half, z, Math.PI / 2);
      place(String(row), -half, z, -Math.PI / 2);
    }
  });
  scene.add(group);
};

const BLOCK_STYLE_LOOKS = {
  "xs-marble": { texture: "walls/marmol-blanco.jpg", roughness: 0.15, clearcoat: 0.8 },
  "xs-emerald": { texture: "walls/marmol-esmeralda.jpg", roughness: 0.15, clearcoat: 0.8 },
  "xs-wood": { texture: "walls/nogal.jpg", roughness: 0.3, clearcoat: 0.4 },
  "xs-gold": { color: "#E2B84A", metalness: 1, roughness: 0.22 },
  "xs-chrome": { color: "#DFE5EC", metalness: 1, roughness: 0.12 },
  "xs-lava": { texture: "walls/lava.jpg", roughness: 0.45, emissive: 0.55 },
  "xs-galaxy": { texture: "walls/galaxia.jpg", roughness: 0.08, clearcoat: 1 },
  "xs-water": { texture: "walls/agua.jpg", normal: "walls/agua-normal.jpg", roughness: 0.1, clearcoat: 1 },
  "xs-grass": { texture: "walls/cesped.jpg", normal: "walls/cesped-normal.jpg", roughness: 0.88 },
  "xs-neon": { color: "#0A0F1E", roughness: 0.28, metalness: 0.2, decal: "sprites/xs-neon.png" },
  "xs-neon-pink": { color: "#0A0F1E", roughness: 0.28, metalness: 0.2, decal: "sprites/xs-neon-pink.png" },
  "xs-star": { plastic: true, decal: "sprites/xs-star.png" },
  "xs-plain": { plastic: true },
};

const DesignModePreview = ({
  boardTheme,
  wallTexture,
  background,
  blockMaterial,
  blockAnimation,
  commonAsset,
  specialAsset,
  commonBlockId,
  specialBlockId,
  boardDesign,
  coordFont = "classic",
  coordScale = 1,
  commonStyle = "",
  specialStyle = "",
  teamColor = "#e11d48",
  assetUrl = (path) => `/design-studio/${path}`,
  partMaterials = {},
  showPins = false,
  activePart = "",
  onPartSelect,
  compact = false,
  autoRotate = false,
  resetSignal = 0,
}) => {
  const mountRef = useRef(null);
  const pinRefs = useRef({});
  const onPartSelectRef = useRef(onPartSelect);
  onPartSelectRef.current = onPartSelect;
  const autoRotateRef = useRef(autoRotate);
  autoRotateRef.current = autoRotate;
  const cameraApiRef = useRef(null);

  const design = boardDesign || { light: "orig", dark: "orig", wall: "orig", trim: "orig" };
  const designKey = [design.light, design.dark, design.wall, design.trim].join("|");
  const partMaterialsKey = JSON.stringify(partMaterials);

  useEffect(() => {
    if (resetSignal && cameraApiRef.current) cameraApiRef.current.reset();
  }, [resetSignal]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let disposed = false;
    let frame = 0;
    const scene = new THREE.Scene();
    scene.background = compact ? null : new THREE.Color("#020814");

    const camera = new THREE.PerspectiveCamera(compact ? 30 : 34, 1, 0.1, 100);
    const defaultCamera = compact ? [15.5, 12.5, 17.5] : [14.5, 11, 17.5];
    camera.position.set(...defaultCamera);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.domElement.className = "xima-design-preview-canvas";
    mount.appendChild(renderer.domElement);

    const environmentGenerator = new THREE.PMREMGenerator(renderer);
    const roomEnvironment = new RoomEnvironment();
    const environmentTarget = environmentGenerator.fromScene(roomEnvironment, 0.04);
    scene.environment = environmentTarget.texture;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.075;
    controls.enablePan = false;
    controls.enableZoom = !compact;
    controls.minDistance = 14;
    controls.maxDistance = 42;
    controls.minPolarAngle = 0.35;
    controls.maxPolarAngle = 1.45;
    controls.target.set(0, 1.5, 0);
    // Zoom suave con la rueda: cada paso mueve poco y la cámara llega deslizándose.
    let zoomTarget = camera.position.distanceTo(controls.target);
    let zoomApplied = zoomTarget;
    const zoomDirection = new THREE.Vector3();
    const handleWheel = (event) => {
      if (compact) return;
      event.preventDefault();
      event.stopPropagation();
      const step = THREE.MathUtils.clamp(event.deltaY, -120, 120) * 0.0011;
      zoomTarget = THREE.MathUtils.clamp(zoomTarget * (1 + step), controls.minDistance, controls.maxDistance);
    };
    mount.addEventListener("wheel", handleWheel, { capture: true, passive: false });
    const updateSmoothZoom = () => {
      const current = camera.position.distanceTo(controls.target);
      // Si otra cosa cambió la distancia (pellizco en el móvil), se sigue desde ahí.
      if (Math.abs(current - zoomApplied) > 0.01) zoomTarget = current;
      const next = current + (zoomTarget - current) * 0.12;
      zoomDirection.subVectors(camera.position, controls.target).normalize();
      camera.position.copy(controls.target).addScaledVector(zoomDirection, next);
      zoomApplied = next;
    };
    cameraApiRef.current = {
      reset: () => {
        camera.position.set(...defaultCamera);
        controls.target.set(0, 1.5, 0);
        controls.update();
        zoomTarget = camera.position.distanceTo(controls.target);
        zoomApplied = zoomTarget;
      },
    };

    scene.add(new THREE.HemisphereLight(0xbcecff, 0x101622, 2.25));
    const keyLight = new THREE.DirectionalLight(0xffffff, 3.4);
    keyLight.position.set(8, 16, 11);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.left = -12;
    keyLight.shadow.camera.right = 12;
    keyLight.shadow.camera.top = 12;
    keyLight.shadow.camera.bottom = -12;
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x39cfff, 2.1);
    rimLight.position.set(-12, 7, -9);
    scene.add(rimLight);

    if (!compact) {
      const starGeometry = new THREE.BufferGeometry();
      const starPositions = [];
      for (let index = 0; index < 750; index += 1) {
        const radius = 27 + (index % 17) * 0.7;
        const theta = index * 2.399963229728653;
        const vertical = -0.72 + ((index * 37) % 145) / 100;
        const horizontalRadius = Math.sqrt(Math.max(0, 1 - vertical * vertical));
        starPositions.push(
          Math.cos(theta) * horizontalRadius * radius,
          vertical * radius,
          Math.sin(theta) * horizontalRadius * radius,
        );
      }
      starGeometry.setAttribute("position", new THREE.Float32BufferAttribute(starPositions, 3));
      scene.add(new THREE.Points(starGeometry, new THREE.PointsMaterial({
        color: 0xbce6ff,
        size: 0.075,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
      })));
    }

    const textureLoader = new THREE.TextureLoader();
    textureLoader.setCrossOrigin("anonymous");
    const gltfLoader = new GLTFLoader();
    const ownedTextures = [];
    const ownedMaterials = [];
    const ownedGeometries = [];
    const companionTextureCache = new Map();
    const mixers = [];
    const gearAnimationCycles = [];
    const proceduralAnimators = [];
    const textureCache = new Map();
    const sharedGeometries = new Set();

    const loadCached = (url, options = {}) => {
      const key = `${url}|${options.colorSpace === false ? "linear" : "srgb"}|${JSON.stringify(options.repeat || 1)}`;
      if (!textureCache.has(key)) textureCache.set(key, loadTexture(textureLoader, url, options));
      return textureCache.get(key);
    };

    // Material de una casilla (clara u oscura) según su parte del diseño.
    const buildTileMaterial = async (part, fallback) => {
      if (isOrig(part)) return fallback();
      const info = partMaterials[part] || {};
      if (part.startsWith("tex:")) {
        const finish = FINISHES[info.finish] || FINISHES.polished;
        const [map, normalMap] = await Promise.all([
          loadCached(info.tile),
          info.normal ? loadCached(info.normal, { colorSpace: false }) : Promise.resolve(null),
        ]);
        [map, normalMap].forEach((texture) => {
          if (!texture) return;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.repeat.set(1, 1);
        });
        return new THREE.MeshPhysicalMaterial({
          map,
          normalMap,
          normalScale: new THREE.Vector2(finish.normalScale || 1, finish.normalScale || 1),
          roughness: finish.roughness,
          metalness: 0,
          clearcoat: finish.clearcoat || 0,
          clearcoatRoughness: finish.clearcoatRoughness || 0.1,
          emissive: finish.emissive ? new THREE.Color(0xffffff) : new THREE.Color(0x000000),
          emissiveMap: finish.emissive ? map : null,
          emissiveIntensity: finish.emissive ? 0.35 : 0,
        });
      }
      if (part.startsWith("col:")) {
        const map = await loadCached(assetUrl("tiles/liso.jpg"));
        if (map) {
          map.wrapS = THREE.ClampToEdgeWrapping;
          map.wrapT = THREE.ClampToEdgeWrapping;
          map.repeat.set(1, 1);
        }
        return new THREE.MeshPhysicalMaterial({
          map,
          color: new THREE.Color(`#${part.slice(4)}`),
          roughness: 0.38,
          clearcoat: 0.3,
        });
      }
      if (part.startsWith("flag:")) {
        const map = await loadCached(info.image, { repeat: ATLAS_GRID });
        return new THREE.MeshStandardMaterial({ map, roughness: 0.55 });
      }
      return fallback();
    };

    const buildWallMaterial = async (part, legacyTexture, legacyNormal) => {
      if (part && part.startsWith("tex:")) {
        const info = partMaterials[part] || {};
        const finish = FINISHES[info.finish] || FINISHES.polished;
        const [map, normalMap] = await Promise.all([
          loadCached(info.wall),
          info.wallNormal ? loadCached(info.wallNormal, { colorSpace: false }) : Promise.resolve(null),
        ]);
        return new THREE.MeshPhysicalMaterial({
          map,
          normalMap,
          roughness: Math.max(0.22, finish.roughness),
          clearcoat: Math.min(0.5, finish.clearcoat || 0),
          emissive: finish.emissive ? new THREE.Color(0xffffff) : new THREE.Color(0x000000),
          emissiveMap: finish.emissive ? map : null,
          emissiveIntensity: finish.emissive ? 0.3 : 0,
          side: THREE.DoubleSide,
        });
      }
      if (part && part.startsWith("col:")) {
        return new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(`#${part.slice(4)}`),
          roughness: 0.45,
          clearcoat: 0.1,
          side: THREE.DoubleSide,
        });
      }
      return new THREE.MeshPhysicalMaterial({
        name: "paredes",
        color: legacyTexture ? 0xffffff : 0xb9eff7,
        map: legacyTexture,
        normalMap: legacyNormal,
        metalness: legacyTexture ? 0.12 : 0.02,
        roughness: legacyTexture ? 0.36 : 0.08,
        transmission: legacyTexture ? 0 : 0.76,
        transparent: !legacyTexture,
        opacity: legacyTexture ? 1 : 0.32,
        thickness: 0.3,
        ior: 1.45,
        side: THREE.DoubleSide,
      });
    };

    const decalTextures = {};
    const getDecal = async (path) => {
      if (!path) return null;
      if (!decalTextures[path]) decalTextures[path] = loadCached(assetUrl(path));
      return decalTextures[path];
    };

    // Cubo de los bloques nuevos (xs-*).
    const buildStyledCube = async (style, position, size, color, forceStar, skipDecal = false) => {
      const look = BLOCK_STYLE_LOOKS[style];
      if (!look) return null;
      const geometry = new RoundedBoxGeometry(size, size, size, 3, size * 0.07);
      ownedGeometries.push(geometry);
      let material;
      if (look.plastic) {
        material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(color),
          roughness: 0.18,
          metalness: 0.05,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
        });
        material.toneMapped = false;
      } else {
        const [map, normalMap] = await Promise.all([
          look.texture ? loadCached(assetUrl(look.texture), { repeat: 0.55 }) : Promise.resolve(null),
          look.normal ? loadCached(assetUrl(look.normal), { colorSpace: false, repeat: 0.55 }) : Promise.resolve(null),
        ]);
        material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(look.color || "#ffffff"),
          map,
          normalMap,
          roughness: look.roughness ?? 0.4,
          metalness: look.metalness || 0,
          clearcoat: look.clearcoat || 0,
          emissive: look.emissive ? new THREE.Color(0xffffff) : new THREE.Color(0x000000),
          emissiveMap: look.emissive ? map : null,
          emissiveIntensity: look.emissive || 0,
        });
      }
      ownedMaterials.push(material);
      const cube = new THREE.Mesh(geometry, material);
      cube.position.set(...position);
      cube.castShadow = true;
      cube.receiveShadow = true;
      scene.add(cube);
      const decalPath = skipDecal ? "" : forceStar ? "sprites/xs-star.png" : look.decal;
      if (decalPath) {
        const decal = await getDecal(decalPath);
        if (!disposed) {
          const isNeon = decalPath.includes("neon");
          addSpecialMarkers(scene, position, isNeon ? size * 1.36 : size, decal, ownedMaterials, isNeon);
        }
      }
      return cube;
    };

    const setupScene = async () => {
      const blockTextureRepeat = resolveBlockTextureRepeat(blockMaterial?.id);
      const wallIsLegacy = isOrig(design.wall);
      const wallBase = !wallIsLegacy || wallTexture?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, wallTexture?.previewTexture, { repeat: wallTexture?.isFlag ? 1 : 4 });
      const wallNormal = !wallIsLegacy || wallTexture?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, wallTexture?.previewNormal, { colorSpace: false, repeat: 4 });
      const usesCommonStyle = Boolean(BLOCK_STYLE_LOOKS[commonStyle]);
      const blockBase = usesCommonStyle
        ? Promise.resolve(null)
        : blockMaterial?.id === "default"
        ? loadTexture(textureLoader, commonBlockId !== "default_star" ? commonAsset : "")
        : loadTexture(textureLoader, blockMaterial?.previewTexture, { repeat: blockTextureRepeat });
      const materialMap = (key) => usesCommonStyle || blockMaterial?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, blockMaterial?.[key], { colorSpace: false, repeat: blockTextureRepeat });
      const animationPromise = !usesCommonStyle && blockAnimation?.id !== "default" && blockAnimation?.model
        ? gltfLoader.loadAsync(blockAnimation.model).catch(() => null)
        : Promise.resolve(null);

      const [
        backgroundTexture,
        selectedWallTexture,
        selectedWallNormal,
        commonTexture,
        commonNormal,
        commonRoughness,
        commonMetalness,
        specialTexture,
        boardGltf,
        animationGltf,
      ] = await Promise.all([
        compact ? Promise.resolve(null) : loadTexture(textureLoader, background?.image),
        wallBase,
        wallNormal,
        blockBase,
        materialMap("previewNormal"),
        materialMap("previewRoughness"),
        materialMap("previewMetalness"),
        BLOCK_STYLE_LOOKS[specialStyle] ? Promise.resolve(null) : loadTexture(textureLoader, specialAsset),
        loadBoardGltf(gltfLoader).catch(() => null),
        animationPromise,
      ]);
      if (disposed) return;

      [
        backgroundTexture,
        selectedWallTexture,
        selectedWallNormal,
        commonTexture,
        commonNormal,
        commonRoughness,
        commonMetalness,
        specialTexture,
      ].forEach((texture) => {
        if (texture) ownedTextures.push(texture);
      });
      if (!compact) {
        if (backgroundTexture) scene.background = backgroundTexture;
        else if (background?.plainColor) scene.background = new THREE.Color(background.plainColor);
      }

      const customTiles = !isOrig(design.light) || !isOrig(design.dark);
      const metalTrim = !isOrig(design.trim);
      const lightMaterial = await buildTileMaterial(design.light, () => new THREE.MeshStandardMaterial({
        name: "casillas claras",
        color: new THREE.Color("#fff7f7"),
        metalness: 0.03,
        roughness: 0.62,
      }));
      const darkMaterial = await buildTileMaterial(design.dark, () => new THREE.MeshPhysicalMaterial({
        name: "casillas oscuras",
        color: new THREE.Color(boardTheme?.dark || "#005c4a"),
        metalness: 0.86,
        roughness: 0.08,
        clearcoat: 1,
        clearcoatRoughness: 0.04,
      }));
      const wallMaterial = await buildWallMaterial(design.wall, selectedWallTexture, selectedWallNormal);
      const trimMaterial = metalTrim
        ? new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(TRIM_COLORS[design.trim] || TRIM_COLORS.gold),
          metalness: 1,
          roughness: 0.2,
          clearcoat: 0.4,
        })
        : new THREE.MeshPhysicalMaterial({
          name: "cantos",
          color: 0x77c9ed,
          metalness: 0.72,
          roughness: 0.1,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
        });
      [lightMaterial, darkMaterial, wallMaterial, trimMaterial].forEach((material) => ownedMaterials.push(material));
      if (disposed) return;

      if (boardGltf?.scene) {
        boardGltf.scene.traverse((child) => {
          if (child.geometry) sharedGeometries.add(child.geometry);
        });
        const board = boardGltf.scene.clone(true);
        board.name = "TABLERO ESPEJO SELECCIONADO";
        // The FBX master stores its board plane in XY. The exported GLB keeps
        // that authored orientation, so rotate only the preview instance into
        // the same Y-up gameplay orientation used by Unity.
        board.rotation.x = Math.PI / 2;
        board.updateMatrixWorld(true);
        const extraMeshes = [];
        board.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;
          const materialName = normalizeMaterialName(child.material?.name);
          if (materialName.includes("casillas oscuras") || materialName.includes("casillas claras")) {
            const isDark = materialName.includes("casillas oscuras");
            const part = isDark ? design.dark : design.light;
            const { visible, covered } = splitCoveredTiles(child.geometry);
            ownedGeometries.push(visible, covered);
            child.geometry = visible;
            if (!isOrig(part)) {
              child.geometry = applyTileUvs(visible);
              ownedGeometries.push(child.geometry);
            }
            child.material = isDark ? darkMaterial : lightMaterial;
            const coveredMaterial = new THREE.MeshStandardMaterial({ color: 0x08090d, roughness: 0.9, metalness: 0 });
            extraMeshes.push({ parent: child.parent, mesh: new THREE.Mesh(covered, coveredMaterial), source: child });
          } else if (materialName.includes("paredes")) {
            if (!isOrig(design.wall)) {
              child.geometry = applyPlanarWallUvs(child.geometry);
              ownedGeometries.push(child.geometry);
            } else if (wallTexture?.isFlag && selectedWallTexture) {
              applyFlagWallUvs(child);
            }
            child.material = wallMaterial;
          } else {
            const { perimeter, strips, wallStrips } = splitTrimGeometry(child.geometry);
            ownedGeometries.push(perimeter, strips, wallStrips);
            child.geometry = perimeter;
            child.material = trimMaterial;
            const mergeWallStrips = !isOrig(design.wall) || metalTrim;
            const wallStripGeometry = mergeWallStrips ? applyPlanarWallUvs(wallStrips) : wallStrips;
            if (mergeWallStrips) ownedGeometries.push(wallStripGeometry);
            extraMeshes.push({
              parent: child.parent,
              mesh: new THREE.Mesh(wallStripGeometry, mergeWallStrips ? wallMaterial : new THREE.MeshPhysicalMaterial({
                name: "cantos",
                color: 0x77c9ed,
                metalness: 0.72,
                roughness: 0.1,
                clearcoat: 1,
                clearcoatRoughness: 0.05,
              })),
              source: child,
              sharedMaterial: mergeWallStrips,
            });
            if (!customTiles) {
              extraMeshes.push({ parent: child.parent, mesh: new THREE.Mesh(strips, new THREE.MeshPhysicalMaterial({
                name: "cantos",
                color: 0x77c9ed,
                metalness: 0.72,
                roughness: 0.1,
                clearcoat: 1,
                clearcoatRoughness: 0.05,
              })), source: child });
            }
          }
        });
        extraMeshes.forEach(({ parent, mesh, source, sharedMaterial }) => {
          if (!sharedMaterial) ownedMaterials.push(mesh.material);
          mesh.position.copy(source.position);
          mesh.quaternion.copy(source.quaternion);
          mesh.scale.copy(source.scale);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          parent.add(mesh);
        });
        scene.add(board);
        mount.dataset.boardLoaded = "true";
      } else {
        mount.dataset.boardLoaded = "false";
      }

      addWallLabels({
        scene,
        trim: metalTrim ? design.trim : "",
        fontId: coordFont,
        scale: coordScale,
        ownedMaterials,
        ownedTextures,
        floating: !metalTrim,
      });

      const addAnimatedBlock = (position, index) => {
        const model = cloneAnimationModel(
          animationGltf.scene,
          position,
          blockAnimation.id,
          ownedMaterials,
          index,
          companionTextureCache,
          ownedTextures,
        );
        model.rotation.y = (index % 4) * Math.PI * 0.5;
        scene.add(model);
        if (animationGltf.animations?.length && MIXER_MODEL_ANIMATIONS.has(blockAnimation.id)) {
          const mixer = new THREE.AnimationMixer(model);
          const actions = animationGltf.animations.map((clip) => mixer.clipAction(clip));
          if (blockAnimation.id === "gear-cube") {
            actions.forEach((action) => {
              action.setLoop(THREE.LoopOnce, 1);
              action.clampWhenFinished = true;
              action.play();
            });
            gearAnimationCycles.push({
              mixer,
              actions,
              duration: Math.max(...animationGltf.animations.map((clip) => clip.duration)),
              elapsed: 0,
              holdRemaining: 0,
            });
          } else {
            actions.forEach((action) => action.play());
            mixers.push(mixer);
          }
        }
        if (PROCEDURAL_MODEL_ANIMATIONS.has(blockAnimation.id)) {
          addProceduralAnimation({
            model,
            animationId: blockAnimation.id,
            phase: index * 0.73,
            ownedMaterials,
            proceduralAnimators,
          });
        }
      };
      if (BLOCK_STYLE_LOOKS[commonStyle]) {
        await Promise.all(
          BLOCK_POSITIONS.map((position) => buildStyledCube(commonStyle, position, 0.9, teamColor, false)),
        );
      } else if (animationGltf?.scene) {
        BLOCK_POSITIONS.forEach(addAnimatedBlock);
      } else {
        const commonColor = blockMaterial?.id === "default" ? new THREE.Color(teamColor) : 0xffffff;
        BLOCK_POSITIONS.forEach((position) => {
          const cube = addPreviewCube({
            scene,
            position,
            color: commonColor,
            texture: commonTexture,
            normalMap: commonNormal,
            roughnessMap: commonRoughness,
            metalnessMap: commonMetalness,
          });
          cube.material.forEach((material) => ownedMaterials.push(material));
        });
      }
      if (disposed) return;

      // Bloque especial: la MISMA base que los comunes (diseño xs-*, animación,
      // material o plástico del color) y encima solo su adorno propio en las
      // seis caras: estrella dorada / neón si es un diseño xs-*, o su imagen.
      const specialLook = BLOCK_STYLE_LOOKS[specialStyle];
      const specialIsNeon = Boolean(specialLook?.decal?.includes("neon"));
      const specialDecal = specialLook
        ? await getDecal(specialIsNeon ? specialLook.decal : "sprites/xs-star.png")
        : specialTexture;
      if (disposed) return;
      if (BLOCK_STYLE_LOOKS[commonStyle]) {
        await buildStyledCube(commonStyle, SPECIAL_POSITION, 0.94, teamColor, false, true);
      } else if (animationGltf?.scene) {
        addAnimatedBlock(SPECIAL_POSITION, 0);
      } else {
        const usesBlockMaterial = blockMaterial?.id !== "default" && Boolean(commonTexture);
        const specialCube = addPreviewCube({
          scene,
          position: SPECIAL_POSITION,
          size: 0.94,
          color: usesBlockMaterial ? 0xffffff : new THREE.Color(teamColor),
          texture: usesBlockMaterial ? commonTexture : null,
          normalMap: usesBlockMaterial ? commonNormal : null,
          roughnessMap: usesBlockMaterial ? commonRoughness : null,
          metalnessMap: usesBlockMaterial ? commonMetalness : null,
          special: !usesBlockMaterial,
        });
        specialCube.material.forEach((material) => ownedMaterials.push(material));
      }
      if (disposed) return;
      addSpecialMarkers(
        scene,
        SPECIAL_POSITION,
        specialIsNeon ? 0.94 * 1.36 : 0.94,
        specialDecal,
        ownedMaterials,
        specialIsNeon,
      );
      mount.dataset.ready = "true";
    };

    setupScene().catch(() => {});

    // Puntos tocables sobre las partes del tablero.
    const pinAnchors = {
      light: new THREE.Vector3(-3 * BOARD_PITCH, 0.52, 6 * BOARD_PITCH),
      dark: new THREE.Vector3(2 * BOARD_PITCH, 2.52, 3 * BOARD_PITCH),
      wall: new THREE.Vector3(-2 * BOARD_PITCH, 1.6, 4.5 * BOARD_PITCH),
      labels: new THREE.Vector3(1 * BOARD_PITCH, 0.05, 6.5 * BOARD_PITCH),
    };
    const projected = new THREE.Vector3();
    const updatePins = (width, height) => {
      Object.entries(pinAnchors).forEach(([part, anchor]) => {
        const element = pinRefs.current[part];
        if (!element) return;
        projected.copy(anchor).project(camera);
        const visible = projected.z < 1;
        const x = ((projected.x + 1) / 2) * width;
        element.style.transform = `translate(${x}px, ${((1 - projected.y) / 2) * height}px)`;
        element.classList.toggle("is-flip", x < 150);
        element.style.opacity = visible ? "1" : "0";
      });
    };

    let viewWidth = 1;
    let viewHeight = 1;
    const resize = () => {
      const bounds = mount.getBoundingClientRect();
      viewWidth = Math.max(compact ? 120 : 320, Math.round(bounds.width));
      viewHeight = Math.max(compact ? 120 : 260, Math.round(bounds.height));
      camera.aspect = viewWidth / viewHeight;
      // En pantallas verticales (móvil) se abre el ángulo para que quepa el tablero.
      camera.fov = (compact ? 30 : 34) * Math.max(1, Math.pow(1.25 / camera.aspect, 0.6));
      camera.updateProjectionMatrix();
      renderer.setSize(viewWidth, viewHeight, false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    const clock = new THREE.Clock();
    let animationElapsed = 0;
    const animate = () => {
      if (disposed) return;
      const delta = Math.min(clock.getDelta(), 0.05);
      animationElapsed += delta;
      controls.autoRotate = Boolean(autoRotateRef.current) || compact;
      controls.autoRotateSpeed = compact ? 0.6 : 1.2;
      controls.update();
      updateSmoothZoom();
      mixers.forEach((mixer) => mixer.update(delta * 0.55));
      gearAnimationCycles.forEach((cycle) => {
        if (cycle.holdRemaining > 0) {
          cycle.holdRemaining = Math.max(0, cycle.holdRemaining - delta);
          if (cycle.holdRemaining === 0) {
            cycle.mixer.stopAllAction();
            cycle.mixer.time = 0;
            cycle.elapsed = 0;
            cycle.actions.forEach((action) => action.reset().play());
          }
          return;
        }

        const animationDelta = delta * GEAR_ANIMATION_SPEED;
        const remaining = Math.max(0, cycle.duration - cycle.elapsed);
        const appliedDelta = Math.min(animationDelta, remaining);
        cycle.mixer.update(appliedDelta);
        cycle.elapsed += appliedDelta;
        if (cycle.elapsed >= cycle.duration - 0.0001) {
          cycle.mixer.setTime(cycle.duration);
          cycle.elapsed = cycle.duration;
          cycle.holdRemaining = GEAR_SQUARED_HOLD_SECONDS;
        }
      });
      proceduralAnimators.forEach((updateAnimation) => updateAnimation(delta, animationElapsed));
      renderer.render(scene, camera);
      if (showPins) updatePins(viewWidth, viewHeight);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      mount.removeEventListener("wheel", handleWheel, { capture: true });
      observer.disconnect();
      controls.dispose();
      mixers.forEach((mixer) => mixer.stopAllAction());
      gearAnimationCycles.forEach((cycle) => cycle.mixer.stopAllAction());
      ownedGeometries.forEach((geometry) => geometry?.dispose?.());
      scene.traverse((child) => {
        if (child.geometry && !sharedGeometries.has(child.geometry)) child.geometry.dispose();
      });
      ownedMaterials.forEach((material) => material?.dispose?.());
      ownedTextures.forEach((texture) => texture?.dispose?.());
      textureCache.forEach((promise) => promise.then((texture) => texture?.dispose?.()).catch(() => {}));
      environmentTarget.dispose();
      environmentGenerator.dispose();
      roomEnvironment.dispose?.();
      renderer.dispose();
      renderer.domElement.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    background,
    blockAnimation,
    blockMaterial,
    boardTheme,
    commonAsset,
    commonBlockId,
    specialAsset,
    specialBlockId,
    wallTexture,
    designKey,
    coordFont,
    coordScale,
    commonStyle,
    specialStyle,
    teamColor,
    partMaterialsKey,
    showPins,
    compact,
  ]);

  const pinParts = [
    { id: "light", label: "Casillas claras", side: "left" },
    { id: "dark", label: "Casillas oscuras" },
    { id: "wall", label: "Paredes", side: "left" },
    { id: "labels", label: "Letras y números" },
  ];

  return (
    <div
      ref={mountRef}
      className={`xima-design-preview-canvas-wrap${compact ? " is-compact" : ""}`}
      role="application"
      aria-label="Vista previa 3D interactiva del tablero y los bloques"
      data-board-model="TABLERO ESPEJO SELECCIONADO"
    >
      {showPins ? (
        <div className="xima-studio-pins">
          {pinParts.map((pin) => (
            <button
              key={pin.id}
              type="button"
              ref={(element) => {
                pinRefs.current[pin.id] = element;
              }}
              className={`xima-studio-pin${pin.side === "left" ? " is-left" : ""}${activePart === pin.id ? " is-active" : ""}`}
              onClick={() => onPartSelectRef.current?.(pin.id)}
            >
              <i aria-hidden="true" />
              <span>{pin.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default DesignModePreview;
